import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api.model';
import { IOrder } from '../models/order.model';

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/order`;

  /**
   * Get paginated orders with search and sorting
   */
  getOrders(
    page: number = 1,
    pageSize: number = 10,
    search: string = '',
    sortField: string = 'internalOrderId',
    sortDirection: string = 'asc'
  ): Observable<ApiResponse<PaginatedResponse<IOrder>>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<ApiResponse<PaginatedResponse<IOrder>>>(`${this.apiUrl}`, { params });
  }

  /**
   * Create a new order
   * @param order Order data
   */
  createOrder(order: IOrder): Observable<ApiResponse<IOrder>> {
    return this.http.post<ApiResponse<IOrder>>(this.apiUrl, order);
  }

  /**
   * Update order information
   * @param id Order ID
   * @param order Updated order data
   */
  updateOrder(id: string, order: IOrder): Observable<IOrder> {
    return this.http.put<{ success: boolean; data: IOrder }>(`${this.apiUrl}/${id}`, order).pipe(
      catchError(error => {
        console.error(`Error updating order ${id}:`, error);
        return throwError(() => new Error('Failed to update order'));
      }),
      map(response => response.data)
    );
  }

  /**
   * Delete an order
   * @param id Order ID
   */
  deleteOrder(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }
}
